import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// svc-10 `/v1/cost-models*` (gateway routes /v1/cost-models -> calculation).
// This is the IMPLEMENTED owner of the full cost-model record: it returns the
// complete constants[] / formulas[] / price-rule blocks and accepts a
// transactional PUT. (The older svc-04 `/v1/admin/get-all-cost-models` pivot
// only exposed 5 fixed constant keys and had no write path — see
// 04_Deliverables/22_Admin_Panel_Issues for the audit.)
//
// Types mirror @penn/shared-types cost-model.schema (kept local per the
// existing api/*.ts convention).

/** Allowed `price_rule_source` values (mirrors PRICE_RULE_SOURCES). */
export const PRICE_RULE_SOURCES = [
  "avg_base_machine",
  "sherex_price_list_min",
  "profil_indi_config_sum",
  "mds_a240_base_plus_robot",
] as const;

export type PriceRuleSource = (typeof PRICE_RULE_SOURCES)[number];

export interface CostModelSummary {
  modelId: string;
  label: string | null;
  priceRuleSource: PriceRuleSource | null;
  additionalInvestment: string | null;
  /** True iff every formula row for this model has eval_order != 100. */
  evalOrderComplete: boolean;
}

export interface CostModelConstant {
  constantKey: string;
  value: string | null;
}

export interface CostModelFormula {
  formulaKey: string;
  expression: string | null;
  evalOrder: number;
}

export interface CostModelPriceRuleFilter {
  filterCategory: string;
  filterSubcategory: string;
}

export interface CostModelPriceRuleKey {
  sherexKey: string;
  sortOrder?: number | null;
}

export interface CostModelDetail {
  modelId: string;
  label: string | null;
  appliesToTargetType: string | null;
  appliesToTargetId: string | null;
  appliesToRegion: string | null;
  currency: string | null;
  priceRuleSource: PriceRuleSource | null;
  additionalInvestment: string | null;
  additionalInvestmentCurrency: string | null;
  constants: CostModelConstant[];
  formulas: CostModelFormula[];
  priceRuleFilters: CostModelPriceRuleFilter[];
  priceRuleKeys: CostModelPriceRuleKey[];
}

/** PUT body — writable blocks (Q-CE-09 + 2026-06-08 priceRuleSource). */
export interface UpdateCostModelBody {
  label?: string | null;
  additionalInvestment?: string | null;
  priceRuleSource?: PriceRuleSource | null;
  constants?: CostModelConstant[];
  formulas?: CostModelFormula[];
  priceRuleFilters?: CostModelPriceRuleFilter[];
  priceRuleKeys?: CostModelPriceRuleKey[];
}

/**
 * POST body — create a new model (2026-06-16). Superset of the PUT body plus
 * the new PK `modelId` and the descriptive identity metadata, which IS writable
 * on create (read-only afterwards).
 */
export interface CreateCostModelBody extends UpdateCostModelBody {
  modelId: string;
  appliesToTargetType?: string | null;
  appliesToTargetId?: string | null;
  appliesToRegion?: string | null;
  currency?: string | null;
  additionalInvestmentCurrency?: string | null;
}

const BASE = "/v1/cost-models";

/** GET /v1/cost-models — summary list of the 13 models. */
export async function listCostModels(): Promise<CostModelSummary[]> {
  const res = await Api.get(BASE);
  const body = unwrap<{ items: CostModelSummary[] }>(res.data);
  return body.items;
}

/** GET /v1/cost-models/:id — full detail. */
export async function getCostModel(id: string): Promise<CostModelDetail> {
  const res = await Api.get(`${BASE}/${encodeURIComponent(id)}`);
  return unwrap<CostModelDetail>(res.data);
}

/** PUT /v1/cost-models/:id — transactional replace of the writable blocks. */
export async function updateCostModel(
  id: string,
  body: UpdateCostModelBody,
): Promise<CostModelDetail> {
  const res = await Api.put(`${BASE}/${encodeURIComponent(id)}`, body);
  return unwrap<CostModelDetail>(res.data);
}

/** POST /v1/cost-models — create a new model (409 on duplicate modelId). */
export async function createCostModel(
  body: CreateCostModelBody,
): Promise<CostModelDetail> {
  const res = await Api.post(BASE, body);
  return unwrap<CostModelDetail>(res.data);
}
